import React, { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ObjectSchema } from 'yup';

import { Space } from '@sravni/react-design-system';
import { useIsPhone, useStyles } from '@sravni/react-utils';

import { OK_TEXTS } from '../../../../../utils/errorDictionary';
import { FormControl } from '../../../../UI/FormControl';
import { TUserChangeHandler } from '../../../../UI/FormControl/types';
import { REVIEW_FIELDS } from '../../../constants/common';
import { TFormInputs } from '../../../types';
import { makeLabelRequired } from '../../../utils/makeLabelRequired';
import { Step } from '../Step/Step';

import styles from './text-step.module.scss';
import { Tips } from './Tips/Tips';

type TTextStepProps = {
  handleUserChange?: TUserChangeHandler;
  schema: ObjectSchema<TFormInputs>;
  isReadOnly: boolean;
};

export const TextStep: React.FC<TTextStepProps> = ({ handleUserChange, schema, isReadOnly }) => {
  const cx = useStyles(styles);
  const isPhone = useIsPhone();

  const [helperTextForTextField, setHelperTextForTextField] = React.useState<string>('');

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const textValue = useWatch({ name: REVIEW_FIELDS.TEXT, control });

  const validateTextForHelperText = useCallback(
    async (value: string) => {
      try {
        await schema.validateAt(REVIEW_FIELDS.TEXT, { [REVIEW_FIELDS.TEXT]: value });
        setHelperTextForTextField(OK_TEXTS.TEXT_GOOD);
      } catch (error) {
        if (error.type === 'min') {
          setHelperTextForTextField(OK_TEXTS.TEXT_NOT_ENOUGH);
        } else {
          setHelperTextForTextField(null);
        }
      }
    },
    [schema],
  );

  useEffect(() => {
    validateTextForHelperText(textValue);
  }, [textValue, validateTextForHelperText]);

  return (
    <Step title={'Напишите отзыв'} className={'h-mt-8'}>
      <Space direction={'vertical'} size={isPhone ? 16 : 24}>
        <Tips />

        <FormControl
          controlType="text"
          name={REVIEW_FIELDS.TITLE}
          control={control}
          error={errors?.[REVIEW_FIELDS.TITLE]}
          label={makeLabelRequired('Тема отзыва')}
          isShowCounter
          handleUserChange={handleUserChange}
          disabled={isReadOnly}
        />
        <FormControl
          controlType="textarea"
          name={REVIEW_FIELDS.TEXT}
          control={control}
          error={errors?.[REVIEW_FIELDS.TEXT]}
          label={makeLabelRequired('Подробно опишите произошедшую ситуацию или свой опыт')}
          fieldClassName={cx('textField')}
          isShowCounter
          helperText={helperTextForTextField}
          handleUserChange={handleUserChange}
          disabled={isReadOnly}
        />
      </Space>
    </Step>
  );
};
